<template>
  <div class="container min-h-screen bg-[#11111b] text-white flex flex-col">
    <Header />
    <main id="hero-section"
      class="p-4 sm:p-6 bg-[url('@/assets/images/background.png')] bg-cover bg-center min-h-screen flex items-center justify-center">
      <div class="w-full max-w-4xl px-4 flex flex-col items-center justify-center text-center z-10">
        <h1 class="home-page text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl font-bold text-transparent">
          {{ heroData.name || 'BORENN Morm' }}
        </h1>
        <p class="text-base sm:text-lg md:text-xl mt-2 sm:mt-4 leading-tight text-white typing-container">
          <span class="auto-type-safe inline-flex align-middle">{{ heroData.types[0] || 'Web Dev' }}</span>
        </p>
      </div>
    </main>

    <!-- Contact Section -->
    <section
      class="py-12 px-4 sm:px-6 md:px-12 bg-[#11111b] border-t border-gray-800 flex items-center justify-center w-full">
      <div class="max-w-6xl mx-auto flex flex-col lg:flex-row items-start gap-8 lg:gap-12 w-full">
        <!-- Contact Info -->
        <div class="w-full lg:w-1/2 space-y-6">
          <h2 class="text-1xl sm:text-2xl md:text-3xl lg:text-3xl font-semibold">Contact Me</h2>

          <div v-if="isContactLoading" class="space-y-4">
            <p class="text-base sm:text-lg">Loading...</p>
          </div>

          <div v-else-if="contactError" class="space-y-4">
            <p class="text-base sm:text-lg text-red-500">{{ contactError }}</p>
          </div>

          <div v-else class="space-y-4 text-sm sm:text-base md:text-lg">
            <div class="flex items-center gap-3">
              <Phone class="w-5 h-5 text-cyan-400" />
              <a href="#" @click.prevent="openPhone" 
                 class="hover:underline hover:text-cyan-400 transition-colors duration-200 cursor-pointer" 
                 :class="{ 'opacity-50 cursor-not-allowed': isOpeningPhone }"
                 :title="isOpeningPhone ? 'Opening phone app...' : `Click to call ${contactData.phone || 'phone number'}`"
                 :disabled="isOpeningPhone">
                {{ isOpeningPhone ? 'Opening phone app...' : (contactData.phone || 'N/A') }}
              </a>
            </div>
            <div class="flex items-center gap-3">
              <Mail class="w-5 h-5 text-cyan-400" />
              <a href="#" @click.prevent="openMail" 
                 class="hover:underline hover:text-cyan-400 transition-colors duration-200 cursor-pointer" 
                 :class="{ 'opacity-50 cursor-not-allowed': isOpeningMail }"
                 :title="isOpeningMail ? 'Opening Gmail...' : `Click to open ${contactData.email || 'email'} in Gmail`"
                 :disabled="isOpeningMail"
                 rel="noopener noreferrer">
                {{ isOpeningMail ? 'Opening Gmail...' : (contactData.email || 'N/A') }}
              </a>
            </div>
            <div class="flex items-center gap-3">
              <MapPin class="w-5 h-5 text-cyan-400" />
              <span>{{ contactData.address || 'N/A' }}</span>
            </div>
          </div>
        </div>

        <!-- Contact Form -->
        <div class="w-full lg:w-1/2">
          <h3 class="text-xl sm:text-2xl md:text-3xl font-semibold mb-6 text-center lg:text-left">Let's Talk
          </h3>

          <form class="space-y-4" @submit.prevent="handleSubmit">
            <div>
              <input type="text" v-model="form.name" placeholder="Name"
                class="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all"
                :class="{ 'border-red-500': errors.name }" @input="clearError('name')">
              <p v-if="errors.name" class="text-red-500 text-sm mt-1">{{ errors.name }}</p>
            </div>

            <div>
              <input type="text" v-model="form.phone" placeholder="Phone (9-11 digits)"
                class="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all"
                :class="{ 'border-red-500': errors.phone }" @input="clearError('phone')">
              <p v-if="errors.phone" class="text-red-500 text-sm mt-1">{{ errors.phone }}</p>
            </div>

            <div>
              <input type="email" v-model="form.email" placeholder="Email"
                class="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all"
                :class="{ 'border-red-500': errors.email }" @input="clearError('email')">
              <p v-if="errors.email" class="text-red-500 text-sm mt-1">{{ errors.email }}</p>
            </div>

            <div>
              <textarea v-model="form.message" placeholder="Message"
                class="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg h-32 resize-y focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all"
                :class="{ 'border-red-500': errors.message }" @input="clearError('message')"></textarea>
              <p v-if="errors.message" class="text-red-500 text-sm mt-1">{{ errors.message }}</p>
            </div>

            <button type="submit"
              class="w-full p-3 bg-cyan-500 text-black font-semibold rounded-lg hover:bg-cyan-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="isSubmitting || isBlocked">
              {{ isSubmitting ? 'Sending...' : isBlocked ? 'Temporarily Blocked' : 'Send' }}
            </button>

            <!-- Rate limit warning -->
            <!-- <div v-if="submitCount > 0 && !isBlocked" class="text-center text-sm text-yellow-400">
              {{ submitCount }}/{{ maxSubmissions }} submissions used this hour
            </div> -->

            <!-- Block warning -->
            <div v-if="isBlocked" class="text-center text-sm text-red-400">
              Too many submissions. Please wait before trying again.
            </div>
          </form>
        </div>
      </div>
    </section>

    <ClientFooter />
  </div>
</template>

<script setup>
import { reactive, onMounted, onUnmounted, ref } from 'vue';
import Typed from 'typed.js';
import ClientFooter from '@/components/ClientFooter.vue';
import Header from '@/components/Header.vue';
import { Phone, Mail, MapPin, Globe, Code, Palette, Smartphone } from 'lucide-vue-next';
import { projectFirestore } from '@/firebase/config.js';
import { getFirestore, setDoc, serverTimestamp, getDocs, collection, query, limit, doc } from 'firebase/firestore';

const heroData = reactive({
  name: null,
  types: []
});
let typedInstance;

// Contact form data
const isContactLoading = ref(true);
const contactError = ref(null);
const isOpeningMail = ref(false);
const isOpeningPhone = ref(false);
const form = reactive({
  name: '',
  phone: '',
  email: '',
  message: '',
  status: true
});
const errors = reactive({
  name: '',
  phone: '',
  email: '',
  message: ''
});
const isSubmitting = ref(false);
const contactData = ref({
  phone: '',
  email: '',
  address: ''
});

const featuredServices = ref([]);
const featuredProjects = ref([]);

// DDOS Protection variables
const submitCount = ref(0);
const lastSubmitTime = ref(0);
const cooldownTime = ref(0);
const maxSubmissions = 3; // Max submissions per hour
const cooldownDuration = 60 * 60 * 1000; // 1 hour in milliseconds
const minTimeBetweenSubmissions = 30 * 1000; // 30 seconds between submissions
const isBlocked = ref(false);
const blockEndTime = ref(0);

// Telegram configuration
const telegramSettings = ref({
  telegramToken: '',
  chatIds: []
});

const loadHeroData = async () => {
  try {
    const heroQuery = query(collection(projectFirestore, 'heroes'), limit(1));
    const querySnapshot = await getDocs(heroQuery);
    if (!querySnapshot.empty) {
      const data = querySnapshot.docs[0].data();
      Object.assign(heroData, {
        name: data.name || null,
        types: Array.isArray(data.type) ? data.type : ['Web Developer']
      });
    }
  } catch (err) {
    console.error('Error loading hero:', err);
  }
};

const loadContactData = async () => {
  try {
    const querySnapshot = await getDocs(query(collection(projectFirestore, 'heroes'), limit(1)));
    if (!querySnapshot.empty) {
      const docData = querySnapshot.docs[0].data();
      contactData.value = {
        phone: docData.phone || '',
        email: docData.email || '',
        address: docData.address || ''
      };
    } else {
      contactError.value = 'No hero data found';
    }
  } catch (err) {
    contactError.value = 'Failed to load contact information';
  } finally {
    isContactLoading.value = false;
  }
};

const loadFeaturedServices = async () => {
  try {
    const servicesQuery = query(collection(projectFirestore, 'services'), limit(3));
    const querySnapshot = await getDocs(servicesQuery);
    featuredServices.value = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      icon: getServiceIcon(doc.data().title)
    }));
  } catch (err) {
    console.error('Error loading services:', err);
  }
};

const loadFeaturedProjects = async () => {
  try {
    const projectsQuery = query(collection(projectFirestore, 'projects'), limit(3));
    const querySnapshot = await getDocs(projectsQuery);
    featuredProjects.value = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      technologies: [...(doc.data().frontends || []), ...(doc.data().backends || []), ...(doc.data().databases || [])]
    }));
  } catch (err) {
    console.error('Error loading projects:', err);
  }
};

const getServiceIcon = (title) => {
  const titleLower = title.toLowerCase();
  if (titleLower.includes('web') || titleLower.includes('frontend')) return Code;
  if (titleLower.includes('design') || titleLower.includes('ui')) return Palette;
  if (titleLower.includes('mobile') || titleLower.includes('app')) return Smartphone;
  return Globe;
};

const loadTelegramSettings = async () => {
  try {
    const settingsQuery = query(collection(projectFirestore, 'settings'), limit(1));
    const querySnapshot = await getDocs(settingsQuery);
    if (!querySnapshot.empty) {
      const data = querySnapshot.docs[0].data();
      telegramSettings.value = {
        telegramToken: data.telegramToken || '',
        chatIds: data.chatIds || []
      };
    } else {
      console.warn('No settings found for Telegram configuration');
    }
  } catch (err) {
    console.error('Error loading Telegram settings:', err);
  }
};

const validateForm = () => {
  let isValid = true;
  errors.name = '';
  errors.phone = '';
  errors.email = '';
  errors.message = '';

  if (!form.name.trim()) {
    errors.name = 'Name is required';
    isValid = false;
  }

  if (!form.phone.trim()) {
    errors.phone = 'Phone is required';
    isValid = false;
  } else if (form.phone.length < 9 || form.phone.length > 11) {
    errors.phone = 'Phone must be between 9 and 11 digits';
    isValid = false;
  }

  if (!form.email.trim()) {
    errors.email = 'Email is required';
    isValid = false;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = 'Invalid email format';
    isValid = false;
  }

  if (!form.message.trim()) {
    errors.message = 'Message is required';
    isValid = false;
  } else if (form.message.length < 10) {
    errors.message = 'Message must be at least 10 characters';
    isValid = false;
  }

  return isValid;
};

// DDOS Protection functions
const checkRateLimit = () => {
  const now = Date.now();

  // Check if user is currently blocked
  if (isBlocked.value && now < blockEndTime.value) {
    const remainingTime = Math.ceil((blockEndTime.value - now) / 1000 / 60);
    throw new Error(`Too many requests. Please wait ${remainingTime} minutes before trying again.`);
  }

  // Reset block if time has passed
  if (isBlocked.value && now >= blockEndTime.value) {
    isBlocked.value = false;
    blockEndTime.value = 0;
    submitCount.value = 0;
  }

  // Check minimum time between submissions
  if (lastSubmitTime.value > 0 && (now - lastSubmitTime.value) < minTimeBetweenSubmissions) {
    const waitTime = Math.ceil((minTimeBetweenSubmissions - (now - lastSubmitTime.value)) / 1000);
    throw new Error(`Please wait ${waitTime} seconds before submitting again.`);
  }

  // Reset counter if cooldown period has passed
  if (cooldownTime.value > 0 && (now - cooldownTime.value) > cooldownDuration) {
    submitCount.value = 0;
    cooldownTime.value = 0;
  }

  // Check submission limit
  if (submitCount.value >= maxSubmissions) {
    isBlocked.value = true;
    blockEndTime.value = now + cooldownDuration;
    throw new Error(`Too many submissions. You are temporarily blocked for 1 hour.`);
  }

  return true;
};

const recordSubmission = () => {
  const now = Date.now();
  submitCount.value += 1;
  lastSubmitTime.value = now;

  if (submitCount.value === 1) {
    cooldownTime.value = now;
  }

  // Store in localStorage for persistence across page reloads
  localStorage.setItem('portfolioSubmitCount', submitCount.value.toString());
  localStorage.setItem('portfolioLastSubmit', lastSubmitTime.value.toString());
  localStorage.setItem('portfolioCooldownTime', cooldownTime.value.toString());

  if (isBlocked.value) {
    localStorage.setItem('portfolioBlockEndTime', blockEndTime.value.toString());
  }
};

const loadSubmissionData = () => {
  const storedCount = localStorage.getItem('portfolioSubmitCount');
  const storedLastSubmit = localStorage.getItem('portfolioLastSubmit');
  const storedCooldownTime = localStorage.getItem('portfolioCooldownTime');
  const storedBlockEndTime = localStorage.getItem('portfolioBlockEndTime');

  if (storedCount) {
    submitCount.value = parseInt(storedCount);
  }

  if (storedLastSubmit) {
    lastSubmitTime.value = parseInt(storedLastSubmit);
  }

  if (storedCooldownTime) {
    cooldownTime.value = parseInt(storedCooldownTime);
  }

  if (storedBlockEndTime) {
    const blockTime = parseInt(storedBlockEndTime);
    if (Date.now() < blockTime) {
      isBlocked.value = true;
      blockEndTime.value = blockTime;
    }
  }
};

// Enhanced validation with additional security checks
const validateFormWithSecurity = () => {
  if (!validateForm()) return false;

  // Check for suspicious patterns
  const suspiciousPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /data:text\/html/i,
    /vbscript:/i
  ];

  const fieldsToCheck = [form.name, form.email, form.message];

  for (const field of fieldsToCheck) {
    for (const pattern of suspiciousPatterns) {
      if (pattern.test(field)) {
        errors.message = 'Invalid content detected. Please remove any scripts or suspicious content.';
        return false;
      }
    }
  }

  // Check for excessive length (potential buffer overflow attempts)
  if (form.name.length > 100) {
    errors.name = 'Name is too long (max 100 characters)';
    return false;
  }

  if (form.message.length > 2000) {
    errors.message = 'Message is too long (max 2000 characters)';
    return false;
  }

  // Check for repeated characters (potential spam)
  const hasExcessiveRepetition = (text) => {
    const repeatedChar = /(.)\1{9,}/; // 10 or more consecutive same characters
    return repeatedChar.test(text);
  };

  if (hasExcessiveRepetition(form.message)) {
    errors.message = 'Message contains excessive repeated characters';
    return false;
  }

  return true;
};

const clearError = (field) => {
  errors[field] = '';
};

const logSubmission = async () => {
  try {
    const messageId = crypto.randomUUID();
    await setDoc(doc(projectFirestore, 'messages', messageId), {
      name: form.name,
      phone: form.phone,
      email: form.email,
      message: form.message,
      status: true,
      createdAt: serverTimestamp(),
    });
    console.log('Message saved to Firebase with ID:', messageId);
  } catch (err) {
    console.error('Log submission error:', err);
    throw err; // Re-throw the error so it can be handled in handleSubmit
  }
};

const sendToTelegram = async (retries = 3, delay = 1000) => {
  const telegramMessage = `
 Portfolio Message:

Name: ${form.name}
Phone: ${form.phone}
Email: ${form.email}
Message: ${form.message}
  `;

  if (!telegramSettings.value.telegramToken) {
    throw new Error('Telegram token not configured');
  }

  if (!telegramSettings.value.chatIds || telegramSettings.value.chatIds.length === 0) {
    throw new Error('No chat IDs configured');
  }

  const url = `https://api.telegram.org/bot${telegramSettings.value.telegramToken}/sendMessage`;
  const errors = [];

  // Send to all chat IDs
  for (const chatId of telegramSettings.value.chatIds) {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chat_id: chatId,
            text: telegramMessage,
            parse_mode: 'HTML'
          }),
          signal: AbortSignal.timeout(15000)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Telegram API error: ${errorData.description || 'Failed to send message'}`);
        }
        break; // Success, move to next chat ID
      } catch (err) {
        console.error(`Attempt ${attempt} failed for chat ID ${chatId}: ${err.message}`);
        if (attempt === retries) {
          errors.push(`Chat ID ${chatId}: ${err.message}`);
        } else {
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
  }

  if (errors.length > 0) {
    throw new Error(`Failed to send to some chats: ${errors.join(', ')}`);
  }
};

const handleSubmit = async () => {
  try {
    // Check rate limiting first
    checkRateLimit();

    // Validate form with security checks
    if (!validateFormWithSecurity()) return;

    isSubmitting.value = true;

    // Record this submission attempt
    recordSubmission();

    // Save to Firebase first
    await logSubmission();
    
    // Then send to Telegram
    await sendToTelegram();

    form.name = '';
    form.phone = '';
    form.email = '';
    form.message = '';

    alert('Message sent successfully! 🎉');
  } catch (err) {
    console.error('Form submission error:', err);
    if (err.message.includes('Telegram')) {
      // If only Telegram failed, still show success since message was saved to Firebase
      alert('Message saved successfully! (Telegram notification failed)');
      form.name = '';
      form.phone = '';
      form.email = '';
      form.message = '';
    } else {
      errors.message = err.message || 'Error sending message. Please try again later.';
    }
  } finally {
    isSubmitting.value = false;
  }
};

// Open mail in Gmail web or Gmail app on mobile when user clicks the email link
const openMail = async (event) => {
  const email = contactData.value.email || '';
  if (!email) return;

  isOpeningMail.value = true;

  // Gmail compose URL (web)
  const gmailWebUrl = `https://mail.google.com/mail/?view=cm&fs=1&tf=1&to=${encodeURIComponent(email)}`;

  // Gmail app URL schemes for mobile platforms
  const gmailAndroid = `googlegmail://co?to=${encodeURIComponent(email)}`;
  const gmailIos = `googlegmail://co?to=${encodeURIComponent(email)}`;

  // Better mobile detection
  const userAgent = navigator.userAgent || '';
  const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const isAndroid = /Android/i.test(userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(userAgent);

  if (isMobile) {
    // For mobile devices, try to open the native Gmail app
    let appUrl = '';
    
    if (isAndroid) {
      appUrl = gmailAndroid;
    } else if (isIOS) {
      appUrl = gmailIos;
    }

    if (appUrl) {
      // Try to open the native app
      try {
        // Create a hidden iframe to test if the app scheme works
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = appUrl;
        document.body.appendChild(iframe);
        
        // Remove the iframe after a short delay
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 100);

        // Set a timeout to fallback to web if app doesn't open
        setTimeout(() => {
          // Check if we're still on the same page (app didn't open)
          if (document.hidden || document.webkitHidden) {
            // App opened successfully, do nothing
            return;
          }
          // Fallback to web Gmail
          window.location.href = gmailWebUrl;
        }, 1000);
      } catch (error) {
        // If there's an error, fallback to web Gmail
        window.location.href = gmailWebUrl;
      }
    } else {
      // Fallback to web Gmail for other mobile devices
      window.location.href = gmailWebUrl;
    }
  } else {
    // Desktop -> open Gmail web compose in a new tab
    window.open(gmailWebUrl, '_blank', 'noopener,noreferrer');
  }

  // Reset loading state after a short delay
  setTimeout(() => {
    isOpeningMail.value = false;
  }, 1000);
};

// Open phone app for calling when user clicks the phone number
const openPhone = async (event) => {
  const phone = contactData.value.phone || '';
  if (!phone) return;

  isOpeningPhone.value = true;

  // Clean the phone number (remove spaces, dashes, parentheses)
  const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
  
  // Add country code if not present (assuming +855 for Cambodia)
  let phoneNumber = cleanPhone;
  if (!phoneNumber.startsWith('+')) {
    // If it starts with 0, replace with +855
    if (phoneNumber.startsWith('0')) {
      phoneNumber = '+855' + phoneNumber.substring(1);
    } else if (phoneNumber.startsWith('855')) {
      phoneNumber = '+' + phoneNumber;
    } else {
      // Assume it's a local number, add +855
      phoneNumber = '+855' + phoneNumber;
    }
  }

  // Better mobile detection
  const userAgent = navigator.userAgent || '';
  const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const isAndroid = /Android/i.test(userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(userAgent);

  if (isMobile) {
    // For mobile devices, try to open the native phone app
    let phoneUrl = '';
    
    if (isAndroid) {
      // Android phone app scheme
      phoneUrl = `tel:${phoneNumber}`;
    } else if (isIOS) {
      // iOS phone app scheme
      phoneUrl = `tel:${phoneNumber}`;
    }

    if (phoneUrl) {
      try {
        // Try to open the native phone app
        window.location.href = phoneUrl;
        
        // Set a timeout to reset the loading state
        setTimeout(() => {
          isOpeningPhone.value = false;
        }, 2000);
      } catch (error) {
        // If there's an error, show a fallback message
        console.error('Error opening phone app:', error);
        alert(`Unable to open phone app. Please call ${phoneNumber} manually.`);
        isOpeningPhone.value = false;
      }
    } else {
      // Fallback for other mobile devices
      alert(`Please call ${phoneNumber} manually.`);
      isOpeningPhone.value = false;
    }
  } else {
    // Desktop -> show a message with the phone number
    alert(`Please call ${phoneNumber} from your phone.`);
    isOpeningPhone.value = false;
  }
};

onMounted(() => {
  // Load submission data from localStorage for rate limiting persistence
  loadSubmissionData();

  loadHeroData().then(() => {
    // Ensure the typing container is ready before initializing Typed.js
    const typingElement = document.querySelector('.auto-type-safe');
    if (typingElement) {
      // Set initial content to prevent layout shift
      typingElement.textContent = heroData.types[0] || 'Web Developer';
      
      typedInstance = new Typed('.auto-type-safe', {
        strings: heroData.types.length > 0 ? heroData.types : ['Web Developer'],
        typeSpeed: 40,
        backSpeed: 20,
        loop: true,
        showCursor: true,
        cursorChar: ' |',
        startDelay: 500, // Add a small delay to ensure layout is stable
        onBegin: () => {
          // Ensure container maintains its size during animation
          const container = document.querySelector('.typing-container');
          if (container) {
            container.style.minHeight = '1.5em';
          }
        }
      });
    }
  });

  loadContactData();
  loadFeaturedServices();
  loadFeaturedProjects();
  loadTelegramSettings();
});

onUnmounted(() => {
  if (typedInstance) typedInstance.destroy();
});
</script>

<style scoped>
.container {
  width: 100%;
  max-width: none;
  min-height: 100vh;
}

.home-page {
  text-align: center;
  z-index: 5;
  color: transparent;
  -webkit-text-stroke: 1px #fff;
  background: url(/src/assets/images/back.png);
  background-size: cover;
  background-position: 0 0;
  -webkit-background-clip: text;
  background-clip: text;
  animation: back 20s linear infinite;
}

.auto-type-safe {
  display: inline-block;
  white-space: nowrap;
  vertical-align: middle;
  min-height: 1.2em;
  min-width: 1ch;
}

.typing-container {
  min-height: 1.5em;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 640px) {
  .home-page {
    font-size: 4rem;
    -webkit-text-stroke: 0.5px #fff;
    background-size: 200%;
    line-height: 1.2;
  }

  .typing-container {
    min-height: 1.8em;
  }

  .auto-type-safe {
    min-height: 1.4em;
  }

  h2 {
    font-size: 1.75rem;
  }

  h3 {
    font-size: 1.25rem;
  }
}

@media (min-width: 641px) and (max-width: 768px) {
  .home-page {
    font-size: 5rem;
    -webkit-text-stroke: 0.5px #fff;
    background-size: 150%;
    line-height: 1.2;
  }

  .typing-container {
    min-height: 1.6em;
  }

  .auto-type-safe {
    min-height: 1.3em;
  }
}

@keyframes back {
  100% {
    background-position: 2000px 0;
  }
}
</style>